import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildRole13ColumnKey,
  getRole13ValidatingControls,
  isRole13CalculatedControl,
  isRole13Empty,
  pickRole13Schema,
  role13AccountingStandardToTaxonomy,
  Role13Schema,
  Role13SchemaControl,
} from './role13-analysis-of-profit-or-loss.rules';
import {
  accountingStandardToXbrlPrefix,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';
import { differsAtCentPrecision } from '../validation-field-key.util';
import {
  resolveDimensionVisibility,
  readSlice,
  VisibilityCell,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 13 (Analysis of Profit or Loss).
 *
 * Mirrors the editor's role13-310100-analysis-of-profit-or-loss.component.ts
 * evaluateSheetStatus() surface:
 *
 *   this.stateService.updateSheetLevelStatus('analysisOfProfitOrLoss', { hasErrors });
 *
 * where `hasErrors` becomes true iff at least one schema row's
 * getRowValidationStatus() returns 'Failed'. A row is 'Failed' when ANY of:
 *
 *   (a) validationErrors has an entry for the row that is NOT purely an API
 *       mismatch ("does not match the extracted sum") — i.e. a real error.
 *   (b) any of the row's four FormControls is invalid and dirty/touched.
 *   (c) the schema marks the row required AND all four columns are empty.
 *
 * In the current MFRS and MPERS role 13 schemas:
 *
 *   • NO control declares `required: true` (searched the JSON).
 *   • NO control declares any `validators` array (no min / max / pattern).
 *   • The component never attaches Validators.required, Validators.min, etc.
 *     in buildForm() — formControls are created with an empty validator list.
 *   • The only populator of validationErrors is validateAgainstApiData(),
 *     which only produces "does not match the extracted sum" messages. Those
 *     messages are the "Suggested Change" category and DO NOT trigger
 *     'Failed' in getRowValidationStatus().
 *
 * So the required-field branch never blocks today, but it carries the LB-001
 * fix-guard pattern (required-field detection from BOTH control.required === true
 * AND validators[{ name: 'required' }]) so any future schema edit that adds a
 * required field gets picked up automatically — matching roles 22 / 21 / 3.
 *
 *   • (MPERS only) ONE cross-sheet `equality` rule —
 *     `ifrs-smes:ReversalOfInventoryWritedown` must tie to the same field on
 *     Role 18 SOCF (the reciprocal of the rule role 18 already enforces against
 *     role 13). A per-visible-column mismatch at cent precision flips
 *     `hasErrors`. See checkRole13EqualityErrors below. The MFRS schema declares
 *     no equality, so this is a no-op for MFRS.
 *
 * ---- API-mismatch "Suggested Change" is deliberately NOT implemented ----
 *
 * validateAgainstApiData() compares each field against
 * stateService.getOriginalSheetData('analysisOfProfitOrLoss') — the per-
 * conversion API baseline. That baseline is NOT part of the pure validation
 * context (sheetData + crossSheetData), and the mount-based
 * evaluateSheetStatus() never flips `hasErrors` for API mismatches — it
 * leaves them as non-blocking warnings surfaced via
 * getRowErrorDescription(). The pure validator mirrors that: hasErrors and
 * hasSuggestedChanges are both driven off real errors only. API-mismatch
 * warnings remain a component-only concern.
 *
 * ---- Schema variant handling --------------------------------------------
 *
 * Role 13 ships TWO form JSONs (MFRS / MPERS). The component picks at mount
 * time via crossRefService.isMFRS(); the pure path gets the same signal
 * through context.accountingStandard (assembled by
 * ConversionValidatorService.buildContext). Selection is delegated to
 * pickRole13Schema so component and validator stay DRY.
 *
 * ---- Cross-sheet posture (Tier 4) ---------------------------------------
 *
 * Role 13 was historically Tier 3 (its cross-sheet reads — Nature / prep /
 * consolidation flags — only drove column VISIBILITY, never the verdict). It is
 * now Tier 4 because the MPERS inventory `equality` consumes a genuine
 * cross-sheet OPERAND (Role 18's same field) that participates in the Failed
 * verdict. Cross-sheet reads:
 *
 *   • filingPrerequisites.NatureOfFinancialStatements + prep flag — column
 *     visibility (which columns to compare), via the shared dimension-visibility
 *     resolver (same NATURE/PREP gates as its P&L sibling role 12).
 *   • statementOfCashFlowsIndirectMethod (Role 18) — the inventory equality
 *     operand.
 *
 * All cross-sheet slices are read defensively; an absent slice degrades to
 * "nothing to compare" (no flag), so the orchestrator may pass `{}` or omit
 * keys without breaking parity.
 */

interface SchemaValidator {
  name?: string;
  conditionalOn?: unknown;
}

interface SchemaControlForRequired {
  qname: string;
  required?: boolean;
  validators?: SchemaValidator[];
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * LB-001 fix-guard: a control is statically required when either
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. This mirrors the role-6 post-fix check so
 * that any future schema edit to role 13 (none exist today) would be
 * picked up automatically without a separate code change.
 */
function hasUnconditionalRequiredViaValidators(
  control: SchemaControlForRequired
): boolean {
  return (
    control.validators?.some(
      (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
    ) ?? false
  );
}

function isStaticallyRequired(control: Role13SchemaControl): boolean {
  return (
    control.required === true ||
    hasUnconditionalRequiredViaValidators(control as SchemaControlForRequired)
  );
}

/**
 * Build the flat set of form-control keys that the component's buildForm()
 * creates for a dualColumn monetary row. Mirrors getXbrlControlName():
 *   {qname}[Consolidated][CurrentYear]
 *   {qname}[Consolidated][PreviousYear]
 *   {qname}[Separate][CurrentYear]
 *   {qname}[Separate][PreviousYear]
 *
 * For non-dualColumn controls (currently none in role 13 schemas), the flat
 * key is just the qname — the same fallback the component would use if the
 * schema introduced one. Keeping the expansion inline so future readers can
 * see the mirror relationship without jumping files.
 */
function buildRowColumnKeys(control: Role13SchemaControl): string[] {
  if (control.ui?.dualColumn) {
    return [
      `${control.qname}[Consolidated][CurrentYear]`,
      `${control.qname}[Consolidated][PreviousYear]`,
      `${control.qname}[Separate][CurrentYear]`,
      `${control.qname}[Separate][PreviousYear]`,
    ];
  }
  return [control.qname];
}

/**
 * Mirror the component's row-level "required field with no value -> Failed"
 * branch in getRowValidationStatus(). A row is flagged only when ALL of its
 * visible columns are empty — matching `hasValue = false` across the loop.
 *
 * Calculated / readonly rows are skipped: their values are produced by the
 * CalculationService after valueChanges settle, and the component never
 * marks them required. If a future schema edit ever sets `required: true`
 * on a calculated row, this skip preserves the behaviour the component
 * would exhibit.
 */
function checkRequiredRowErrors(
  schema: Role13Schema,
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const control of getRole13ValidatingControls(schema)) {
    if (!isStaticallyRequired(control)) continue;
    if (isRole13CalculatedControl(control)) continue;

    const keys = buildRowColumnKeys(control);
    const anyFilled = keys.some((key) => !isEmpty(sheetData[key]));
    if (!anyFilled) {
      // Flag the row by its base qname (same identifier the component's
      // evaluateSheetStatus loop iterates).
      errors[control.qname] = `${control.qname} is required`;
    }
  }

  return errors;
}

function coerceNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Schema-declared cross-sheet equality (MPERS only).
 *
 * Mirrors role 18's `checkEqualityErrors` and the column-aware standard-equality
 * branch of `EqualityValidationService.validateAllEqualityRules` — so the
 * headless verdict and the mounted editor agree. Today the only role 13
 * equality rule is `ifrs-smes:ReversalOfInventoryWritedown` →
 * `statementOfCashFlowsIndirectMethod` (the reciprocal of the rule role 18
 * already enforces against role 13). The MFRS schema declares no equality, so
 * this is a no-op for MFRS filings — the rule is MPERS-gated by schema
 * presence, exactly like role 18.
 *
 * Cross-sheet operand (Role 18) is read defensively from `crossSheetData`;
 * an absent slice means nothing to compare against → no flag. The inventory
 * field is a plain input on both sheets (not a calculated total), so the raw
 * slice is the correct operand — no recompute needed.
 */
function checkRole13EqualityErrors(
  schema: Role13Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  visibleColumns: ReadonlyArray<VisibilityCell>,
  taxonomy: 'MFRS' | 'MPERS'
): boolean {
  for (const control of schema.controls) {
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;

    // Standard equality only — role 13 declares no sum rules.
    if (rules.every((rule) => rule.operation === 'sum')) continue;

    for (const rule of rules) {
      if (rule.taxonomy && rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()) {
        continue;
      }

      const isSameSheet = rule.sheet === SHEET_KEYS.analysisOfProfitOrLoss;
      const sourcePayload = isSameSheet
        ? sheetData
        : context.crossSheetData?.get(rule.sheet as any);
      if (!isSameSheet && !sourcePayload) continue;

      for (const column of visibleColumns) {
        if (!column.visible) continue;

        const localKey = buildRole13ColumnKey(control.qname, column.dimension, column.period);
        const sourceKey = buildRole13ColumnKey(rule.field, column.dimension, column.period);

        const rawLocal = sheetData[localKey];
        const rawSource = (sourcePayload as Record<string, any> | undefined)?.[sourceKey];

        const localEmpty = isRole13Empty(rawLocal);
        const sourceEmpty = isRole13Empty(rawSource);

        // Skip when both empty (nothing to compare).
        if (localEmpty && sourceEmpty) continue;
        if (rule.skipIfSourceNull && localEmpty) continue;
        if (rule.skipIfTargetNull && sourceEmpty) continue;

        const localNum = coerceNumber(rawLocal) ?? 0;
        const sourceNum = coerceNumber(rawSource) ?? 0;

        if (differsAtCentPrecision(localNum, sourceNum)) {
          return true;
        }
      }
    }
  }
  return false;
}

export const role13AnalysisOfProfitOrLossValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.analysisOfProfitOrLoss,
  // Tier 3 per phase-2 plan: role 13 sits on the cross-sheet dependency
  // graph (see SheetDependencyService — Role 12 invalidates when role 13
  // changes). From role 13's OWN validation perspective the cross-sheet
  // reads only influence column visibility, not error arming, so the
  // validator body is deterministic on sheetData alone today. The Tier 3
  // classification stays for orchestrator parity-harness purposes and to
  // document the graph position — a future schema edit that introduces a
  // genuine cross-sheet required-when rule would plug in here without
  // changing the tier.
  // Tier 4: role 13 now consumes a cross-sheet operand — the MPERS-only
  // `ReversalOfInventoryWritedown` equality against Role 18 SOCF (the reciprocal
  // of the rule role 18 already enforces). The cross-sheet read participates in
  // the Failed verdict, so the dual-path parity harness is mandatory.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole13Schema(context.accountingStandard);

    const errors: Record<string, string> = {
      ...checkRequiredRowErrors(schema, sheetData),
    };

    if (Object.keys(errors).length > 0) {
      return { hasErrors: true, hasSuggestedChanges: false, errors };
    }

    // Cross-sheet equality (MPERS only): the inventory write-down line must tie
    // to the same field on Role 18 SOCF. Column visibility comes from the shared
    // resolver (same NATURE/PREP gates as its P&L sibling role 12), so only
    // columns the editor shows are compared. No-op for MFRS (no equality in the
    // MFRS schema) and when the Role 18 slice is absent.
    const taxonomy = role13AccountingStandardToTaxonomy(context.accountingStandard);
    const visibleColumns = resolveDimensionVisibility(
      getDimensionVisibilityConfig(SHEET_KEYS.analysisOfProfitOrLoss)!,
      {
        filingPrerequisites: readSlice(context.crossSheetData, 'filingPrerequisites'),
        filingInfo: readSlice(context.crossSheetData, 'filingInfo'),
      }
    );
    if (checkRole13EqualityErrors(schema, sheetData, context, visibleColumns, taxonomy)) {
      return { hasErrors: true, hasSuggestedChanges: false };
    }

    // AS-842 calc-diff: surface `Validated*` in the silent path by diffing
    // role 13's recomputed totals against persisted (N8N-extracted) values.
    const recomputed = calculator.calculateTotals_310100(
      sheetData,
      accountingStandardToXbrlPrefix(context.accountingStandard)
    );
    return {
      hasErrors: false,
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, recomputed),
      errors,
    };
  },
};
